import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach admin token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sibo_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401 from admin endpoints, clear stale token and redirect to login.
// We give the toast ~1.6s to be seen so the user understands what happened,
// and preserve their draft (editors autosave to localStorage separately).
let sessionExpiryHandled = false;
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    const isAdminApi = url.startsWith("/admin") || url.startsWith("/auth/me");
    if (status === 401 && isAdminApi && !sessionExpiryHandled) {
      const isOnAdminPage = window.location.pathname.startsWith("/admin");
      const wasLoggedIn = !!localStorage.getItem("sibo_admin_token");
      const onLoginPage = window.location.pathname.endsWith("/admin/login");
      localStorage.removeItem("sibo_admin_token");
      if (isOnAdminPage && wasLoggedIn && !onLoginPage) {
        sessionExpiryHandled = true;
        // Replace the raw "Not authenticated" detail with a friendly message
        // so the user knows their draft is safe.
        try {
          if (err.response?.data) err.response.data.detail = "Session expired — your draft is safe. Sign in again to save.";
        } catch (_) {}
        toast.error("Session expired — your draft is saved locally. Sign in again to publish.", { duration: 3200 });
        const back = encodeURIComponent(window.location.pathname + window.location.search);
        setTimeout(() => {
          window.location.href = `/admin/login?back=${back}`;
        }, 1600);
      }
    }
    return Promise.reject(err);
  }
);

// Public
export async function getCategories() { return (await api.get("/categories")).data; }
export async function getArticles(params = {}) { return (await api.get("/articles", { params })).data; }
export async function getArticle(slug) { return (await api.get(`/articles/${slug}`)).data; }
export async function getTopic(slug) { return (await api.get(`/topics/${slug}`)).data; }
export async function getResources() { return (await api.get("/resources")).data; }
export async function getResource(slug) { return (await api.get(`/resources/${slug}`)).data; }
export async function getFaqs(category) { return (await api.get("/faqs", { params: category ? { category } : {} })).data; }
export async function getFaqCategories() { return (await api.get("/faqs/categories")).data; }
export async function subscribeNewsletter(email, source = "website") { return (await api.post("/newsletter/subscribe", { email, source })).data; }
export async function requestFreeDownload(slug, email) { return (await api.post(`/downloads/free/${slug}`, { email })).data; }
export async function createCheckout(resource_slug, origin_url, email = null) { return (await api.post("/checkout/session", { resource_slug, origin_url, email })).data; }
export async function getCheckoutStatus(session_id) { return (await api.get(`/checkout/status/${session_id}`)).data; }

// Auth
export async function login(email, password) { return (await api.post("/auth/login", { email, password })).data; }
export async function me() { return (await api.get("/auth/me")).data; }

// Admin: Articles
export async function adminListArticles() { return (await api.get("/admin/articles")).data; }
export async function adminGetArticle(slug) { return (await api.get(`/admin/articles/${slug}`)).data; }
export async function adminCreateArticle(payload) { return (await api.post("/admin/articles", payload)).data; }
export async function adminUpdateArticle(slug, payload) { return (await api.put(`/admin/articles/${slug}`, payload)).data; }
export async function adminDeleteArticle(slug) { return (await api.delete(`/admin/articles/${slug}`)).data; }

// Admin: Resources
export async function adminListResources() { return (await api.get("/admin/resources")).data; }
export async function adminGetResource(slug) { return (await api.get(`/admin/resources/${slug}`)).data; }
export async function adminCreateResource(payload) { return (await api.post("/admin/resources", payload)).data; }
export async function adminUpdateResource(slug, payload) { return (await api.put(`/admin/resources/${slug}`, payload)).data; }
export async function adminDeleteResource(slug) { return (await api.delete(`/admin/resources/${slug}`)).data; }
export async function adminUploadPdf(file, onProgress) {
  const fd = new FormData(); fd.append("file", file);
  return (await api.post("/admin/resources/upload/pdf", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  })).data;
}
export async function adminUploadImage(file, onProgress) {
  const fd = new FormData(); fd.append("file", file);
  return (await api.post("/admin/resources/upload/image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  })).data;
}
export async function adminUploadArticlePdf(file, onProgress) {
  const fd = new FormData(); fd.append("file", file);
  return (await api.post("/admin/articles/upload/pdf", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  })).data;
}
// Admin: FAQs
export async function adminListFaqs() { return (await api.get("/admin/faqs")).data; }
export async function adminGetFaq(id) { return (await api.get(`/admin/faqs/${id}`)).data; }
export async function adminCreateFaq(payload) { return (await api.post("/admin/faqs", payload)).data; }
export async function adminUpdateFaq(id, payload) { return (await api.put(`/admin/faqs/${id}`, payload)).data; }
export async function adminDeleteFaq(id) { return (await api.delete(`/admin/faqs/${id}`)).data; }
export async function adminBulkFaqs(list) { return (await api.post("/admin/faqs/bulk", list)).data; }
